import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { ChartFilter, ChartFilterOp, StackByOption } from "@/types/breeze/ChartConfiguration";

interface Props {
  dimensions: StackByOption[];
  value: ChartFilter[] | null | undefined;
  onChange: (filters: ChartFilter[]) => void;
}

const OPERATORS: { value: ChartFilterOp; label: string }[] = [
  { value: "eq", label: "equals" },
];

export default function PlatformFilterBuilder({ dimensions, value, onChange }: Props) {
  const filters = value ?? [];

  const setRow = (index: number, patch: Partial<ChartFilter>) => {
    onChange(filters.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };
  const removeRow = (index: number) => onChange(filters.filter((_, i) => i !== index));
  const addRow = () => onChange([...filters, { field: dimensions[0]?.id ?? "", op: "eq", value: "" }]);

  if (dimensions.length === 0) {
    return (
      <Flex direction="column" gap="2">
        <Text as="label" size="2" weight="medium">Filters</Text>
        <Text size="1" color="gray">This metric has no filterable dimensions.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2">
      <Flex justify="between" align="center">
        <Text as="label" size="2" weight="medium">Filters</Text>
        <Button size="1" variant="soft" type="button" onClick={addRow}>
          <Plus size={12} /> Add filter
        </Button>
      </Flex>
      {filters.length === 0 && (
        <Text size="1" color="gray">No filters — all records included.</Text>
      )}
      {filters.map((filter, i) => {
        const dim = dimensions.find((d) => d.id === filter.field);
        const hasKnownValues = (dim?.values?.length ?? 0) > 0;
        return (
          <Flex key={i} gap="2" align="center" wrap="wrap">
            <Box style={{ flex: "1 1 200px", minWidth: 180 }}>
              <SearchableSelect
                value={filter.field}
                onChange={(v) => setRow(i, { field: v, value: "" })}
                options={dimensions.map((d) => ({ value: d.id, label: d.label }))}
                placeholder="Dimension"
                width={220}
              />
            </Box>
            <Box style={{ flex: "0 0 140px" }}>
              <SearchableSelect
                value={filter.op}
                onChange={(v) => setRow(i, { op: v as ChartFilterOp })}
                options={OPERATORS}
                placeholder="Operator"
                width={140}
              />
            </Box>
            <Box style={{ flex: "1 1 200px", minWidth: 160 }}>
              {hasKnownValues ? (
                <SearchableSelect
                  value={filter.value ?? ""}
                  onChange={(v) => setRow(i, { value: v })}
                  options={dim!.values!.map((v) => ({ value: v, label: v }))}
                  placeholder="Value"
                  width={200}
                />
              ) : (
                <TextField.Root
                  value={filter.value ?? ""}
                  onChange={(e) => setRow(i, { value: e.target.value })}
                  placeholder="Value"
                />
              )}
            </Box>
            <Button
              size="1"
              variant="soft"
              color="red"
              type="button"
              onClick={() => removeRow(i)}
              aria-label="Remove filter"
            >
              <Trash2 size={12} />
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
}
